import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, CalendarDays, Users, Pencil, X } from "lucide-react";
import { api, LIBRARIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LIB_ADDRESS = {
  "Clark County Library": "1401 E. Flamingo Rd, Las Vegas, NV 89119",
  "West Las Vegas Library": "951 W. Lake Mead Blvd, Las Vegas, NV 89106",
  "Sahara West Library": "9600 W. Sahara Ave, Las Vegas, NV 89117",
  "Whitney Library": "5175 E. Tropicana Ave, Las Vegas, NV 89122",
  "Meadows Library": "4300 Meadows Ln, Las Vegas, NV 89107",
};
const COLORS = ["teal", "pink", "green", "yellow"];
const emptyEvent = { date: "", library_name: "", address: "", time: "9:00 AM – 3:00 PM", color: "teal" };

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("calendar");
  const [events, setEvents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [editingId, setEditingId] = useState(null);

  const loadEvents = () => api.get("/calendar").then((r) => setEvents(r.data));
  const loadEnrollments = () => api.get("/enrollments").then((r) => setEnrollments(r.data));

  useEffect(() => {
    loadEvents();
    loadEnrollments();
  }, []);

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const setLibrary = (name) =>
    setForm((f) => ({ ...f, library_name: name, address: LIB_ADDRESS[name] || f.address }));

  const resetForm = () => {
    setForm(emptyEvent);
    setEditingId(null);
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (!form.date || !form.library_name) {
      toast.error("Date and library are required.");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/calendar/${editingId}`, form);
        toast.success("Camp date updated!");
      } else {
        await api.post("/calendar", form);
        toast.success("Camp date added!");
      }
      resetForm();
      loadEvents();
    } catch {
      toast.error("Could not save. Please try again.");
    }
  };

  const editEvent = (ev) => {
    setEditingId(ev.id);
    setForm({ date: ev.date, library_name: ev.library_name, address: ev.address, time: ev.time, color: ev.color });
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this camp date?")) return;
    await api.delete(`/calendar/${id}`);
    toast.success("Deleted.");
    if (editingId === id) resetForm();
    loadEvents();
  };

  const deleteEnrollment = async (id) => {
    if (!window.confirm("Delete this enrollment?")) return;
    await api.delete(`/enrollments/${id}`);
    toast.success("Deleted.");
    loadEnrollments();
  };

  const inputCls = "w-full rounded-xl border-2 border-teal-200 focus:border-pink-400 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-pink-300";

  return (
    <div className="min-h-screen bg-yellow-50 font-body">
      <header className="bg-emerald-800 text-yellow-50 px-6 py-4 flex items-center justify-between">
        <h1 className="font-display font-bold text-xl">WonderStarzLV Admin</h1>
        <div className="flex items-center gap-4">
          <span className="text-sm hidden sm:block">{admin?.email}</span>
          <button data-testid="admin-logout-btn" onClick={onLogout} className="flex items-center gap-1 bg-pink-500 hover:bg-pink-600 px-4 py-2 rounded-full font-display font-bold text-sm transition">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex gap-3 mb-8">
          <button data-testid="tab-calendar" onClick={() => setTab("calendar")} className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-display font-bold transition ${tab === "calendar" ? "bg-teal-400 text-white" : "bg-white text-teal-600 border-2 border-teal-200"}`}>
            <CalendarDays className="w-4 h-4" /> Calendar ({events.length})
          </button>
          <button data-testid="tab-enrollments" onClick={() => setTab("enrollments")} className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-display font-bold transition ${tab === "enrollments" ? "bg-pink-500 text-white" : "bg-white text-pink-500 border-2 border-pink-200"}`}>
            <Users className="w-4 h-4" /> Enrollments ({enrollments.length})
          </button>
        </div>

        {tab === "calendar" && (
          <div className="grid lg:grid-cols-2 gap-8">
            <form data-testid="event-form" onSubmit={saveEvent} className="bg-white rounded-3xl p-6 shadow-pop-teal border-4 border-teal-100 h-fit">
              <h2 className="font-display font-bold text-2xl text-teal-600 mb-4 flex items-center gap-2">
                {editingId ? <><Pencil className="w-5 h-5" /> Edit Date</> : <><Plus className="w-5 h-5" /> Add Camp Date</>}
              </h2>
              <label className="block font-display font-semibold text-emerald-800 mb-1 text-sm">Date</label>
              <input data-testid="event-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={`${inputCls} mb-3`} />

              <label className="block font-display font-semibold text-emerald-800 mb-1 text-sm">Library</label>
              <select data-testid="event-library" value={form.library_name} onChange={(e) => setLibrary(e.target.value)} className={`${inputCls} mb-3`}>
                <option value="">Choose a library</option>
                {LIBRARIES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>

              <label className="block font-display font-semibold text-emerald-800 mb-1 text-sm">Address</label>
              <input data-testid="event-address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className={`${inputCls} mb-3`} placeholder="Street address" />

              <label className="block font-display font-semibold text-emerald-800 mb-1 text-sm">Time</label>
              <input data-testid="event-time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className={`${inputCls} mb-3`} />

              <label className="block font-display font-semibold text-emerald-800 mb-1 text-sm">Dot color</label>
              <div className="flex gap-3 mb-5">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm((f) => ({ ...f, color: c }))} className={`w-9 h-9 rounded-full border-4 ${form.color === c ? "border-emerald-800" : "border-transparent"} ${c === "teal" ? "bg-teal-400" : c === "pink" ? "bg-pink-500" : c === "green" ? "bg-emerald-500" : "bg-yellow-400"}`} aria-label={c} />
                ))}
              </div>

              <div className="flex gap-3">
                <button data-testid="event-save-btn" type="submit" className="flex-1 bg-teal-400 text-white font-display font-bold py-3 rounded-full hover:bg-teal-500 transition">
                  {editingId ? "Update" : "Add Date"}
                </button>
                {editingId && (
                  <button type="button" onClick={resetForm} className="px-4 bg-gray-200 text-gray-700 font-display font-bold py-3 rounded-full hover:bg-gray-300 transition flex items-center gap-1">
                    <X className="w-4 h-4" /> Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="space-y-3">
              {events.length === 0 && <p className="text-emerald-700 font-medium">No camp dates yet. Add one!</p>}
              {events.map((ev) => (
                <div key={ev.id} data-testid={`admin-event-${ev.date}`} className="bg-white rounded-2xl p-4 flex items-start justify-between gap-3 shadow-sm">
                  <div className="flex items-start gap-3">
                    <span className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${ev.color === "teal" ? "bg-teal-400" : ev.color === "pink" ? "bg-pink-500" : ev.color === "green" ? "bg-emerald-500" : "bg-yellow-400"}`} />
                    <div>
                      <p className="font-display font-bold text-emerald-900">{ev.date}</p>
                      <p className="font-semibold text-teal-600">{ev.library_name}</p>
                      <p className="text-sm text-emerald-700">{ev.address}</p>
                      <p className="text-sm text-emerald-700">{ev.time}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`edit-event-${ev.date}`} onClick={() => editEvent(ev)} className="text-teal-500 hover:text-teal-700"><Pencil className="w-5 h-5" /></button>
                    <button data-testid={`delete-event-${ev.date}`} onClick={() => deleteEvent(ev.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-5 h-5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "enrollments" && (
          <div className="space-y-4">
            {enrollments.length === 0 && <p className="text-emerald-700 font-medium">No enrollments yet.</p>}
            {enrollments.map((en) => (
              <div key={en.id} data-testid={`enrollment-${en.id}`} className="bg-white rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display font-bold text-lg text-pink-500">
                      {(en.children || []).map((c) => `${c.name} (${c.age})`).join(", ") || "—"}
                    </h3>
                    <p className="text-sm text-emerald-700">Parent: {en.parent_name} · {en.email} · {en.phone}</p>
                  </div>
                  <button data-testid={`delete-enrollment-${en.id}`} onClick={() => deleteEnrollment(en.id)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-5 h-5" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm text-emerald-800">
                  <p><span className="font-semibold">Children:</span> {(en.children || []).length}</p>
                  <p><span className="font-semibold">Library:</span> {en.library_location}</p>
                  <p><span className="font-semibold">Camp date:</span> {en.camp_date}</p>
                  <p><span className="font-semibold">Emergency:</span> {en.emergency_name || "—"} {en.emergency_phone}</p>
                  <p className="sm:col-span-2"><span className="font-semibold">Notes:</span> {en.notes || "—"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
